import { create } from 'zustand';
import { SeatingTabData } from 'Services/Api/_HumanWritten/SeatingService';

interface SeatingState {
	/**
	 * The seating data for the trip the wizard currently has selected, keyed by trip so a booker
	 * changing trips cannot be shown the previous vessel's seats.
	 */
	byTripId: Record<string, SeatingTabData>;
	setForTrip: (tripId: string, data: SeatingTabData) => void;
	/**
	 * Which sailing the Seats step is showing.
	 *
	 * <p>Here rather than in the step's own state because the wizard's `Continue` drives it: on a
	 * return booking the first press moves to the homeward leg instead of leaving the step, and the
	 * button lives outside the step that owns the tabs.</p>
	 */
	leg: 'departing' | 'returning';
	setLeg: (leg: 'departing' | 'returning') => void;
	/**
	 * Which unseated passengers the booker has been warned about, as
	 * `unseatedFreeSeatSignature` writes it, or null where no warning is showing.
	 *
	 * <p>Here for the same reason as `leg`: `Continue` raises the warning and the step draws it.
	 * Pressing `Continue` again past the same warning goes through.</p>
	 */
	seatWarningFor: string | null;
	setSeatWarningFor: (signature: string | null) => void;
}

/**
 * Seating data, readable synchronously.
 *
 * <p>The wizard rebuilds its step list on every render and decides visibility and validation
 * synchronously, so it cannot await a fetch. `useSeatingTab` performs the fetch and writes the
 * result here; the gating helpers read it with `.getState()` — the same arrangement
 * `useDynamicBookingFieldsStore` uses for the Details step.</p>
 */
export const useSeatingStore = create<SeatingState>(set => ({
	byTripId: {},
	setForTrip: (tripId, data) => set(state => ({ byTripId: { ...state.byTripId, [tripId]: data } })),
	leg: 'departing',
	setLeg: leg => set({ leg }),
	seatWarningFor: null,
	setSeatWarningFor: seatWarningFor => set({ seatWarningFor }),
}));
